import { Fragment, forwardRef } from 'react';

import {
  LinearProgress,
  TableCell,
  TableCellProps,
  Typography,
  alpha,
  capitalize,
  withStyles,
} from '@material-ui/core';

import {
  formatDateFuture,
  formatDuration,
} from '@spotify/confidence-core-react';
import {
  AbTestListItemFragment,
  WorkflowV1WorkflowInstanceCheckState,
} from '@spotify/confidence-graphql';
import { usePlanningDates } from '@spotify/plugin-confidence-workflows-module-planning';

export const Bullet = () => <Fragment> &bull; </Fragment>;

const StyledLinearProgress = withStyles(theme => ({
  root: {
    // Reserve some space to the right so that it looks more aligned with the text
    // in the cell to the right.
    marginRight: theme.spacing(3),
    // Some space between the progress bar and the text, without wrappinge
    // everything in a flex container.
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  colorSecondary: {
    backgroundColor:
      theme.palette.type === 'light'
        ? alpha(theme.palette.secondary.main, 0.1)
        : alpha(theme.palette.secondary.main, 0.3),
  },
}))(LinearProgress);

interface ProgressCellProps extends TableCellProps {
  workflowInstance: Pick<
    AbTestListItemFragment,
    'checks' | 'moduleData2' | 'state'
  >;
}

export const ProgressCell = forwardRef<HTMLTableCellElement, ProgressCellProps>(
  ({ workflowInstance, ...props }, ref) => {
    const { state } = workflowInstance;
    const {
      currentRuntime,
      actualRuntime,
      plannedStartDate,
      remainingDays,
      progress,
    } = usePlanningDates(workflowInstance.moduleData2.planning);

    const isIncomplete = workflowInstance.checks.some(
      c => c.state === WorkflowV1WorkflowInstanceCheckState.Failed,
    );

    // "Finished", "Aborted", "Shipped", ...
    const outcome = capitalize(
      workflowInstance.moduleData2.decision?.outcome?.toString() ?? 'Finished',
    );

    return (
      <TableCell {...props} ref={ref}>
        {state === 'draft' && (
          <Fragment>
            {isIncomplete ? 'A few things left' : 'Ready'}

            {plannedStartDate && !isIncomplete && (
              <Typography
                variant="inherit"
                color="textSecondary"
                component="span"
              >
                <Bullet />
                {formatDateFuture(plannedStartDate)}
              </Typography>
            )}
          </Fragment>
        )}
        {(state === 'ended' || state === 'archived') && (
          <Fragment>
            {outcome}
            {actualRuntime && (
              <Typography
                variant="inherit"
                color="textSecondary"
                component="span"
              >
                <Bullet />
                {formatDuration(actualRuntime)}
              </Typography>
            )}
          </Fragment>
        )}
        {state === 'live' && (
          <Fragment>
            {progress !== null && (
              <Fragment>
                <StyledLinearProgress
                  variant="determinate"
                  value={progress}
                  color="secondary"
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  component="div"
                  noWrap
                >
                  {progress}%
                  {remainingDays !== null && remainingDays === 0 && (
                    <Fragment>
                      <Bullet />
                      Ending today
                    </Fragment>
                  )}
                  {remainingDays !== null && remainingDays === 1 && (
                    <Fragment>
                      <Bullet />
                      Ending tomorrow
                    </Fragment>
                  )}
                  {remainingDays !== null && remainingDays > 1 && (
                    <Fragment>
                      <Bullet />
                      {formatDuration(remainingDays)} left
                    </Fragment>
                  )}
                  {remainingDays !== null && remainingDays < 0 && (
                    <Fragment>
                      <Bullet />
                      {formatDuration(Math.abs(remainingDays))} overdue
                    </Fragment>
                  )}
                </Typography>
              </Fragment>
            )}
            {progress === null && (
              <Typography variant="inherit">
                {formatDuration(currentRuntime)}
              </Typography>
            )}
          </Fragment>
        )}
      </TableCell>
    );
  },
);
